import React, { useEffect, useState } from "react";
import { doc, getDoc, collection, query, where, getDocs, limit, orderBy } from "firebase/firestore";
import { MdAccountCircle, MdCreditCard, MdDateRange, MdAttachMoney, MdCheck, MdClose } from "react-icons/md";
import { db } from "../firebase/firebaseConfig";
import "../styles/Profile.css"; // Usa o CSS que você enviou

const Profile = ({ user, onNavigate }) => {
  const [userData, setUserData] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [planDetails, setPlanDetails] = useState(null);
  const [lastPaymentValue, setLastPaymentValue] = useState(0);
  
  const [counts, setCounts] = useState({
    condominiums: 0,
    towers: 0,
    areas: 0,
    assets: 0
  });
  
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.uid) {
        setLoading(false);
        return;
      }

      try {
        const userRef = doc(db, "users", user.uid);

        // 1. DADOS DO USUÁRIO
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          setUserData(userDoc.data());
        } else {
          setUserData({ email: user.email, createdAt: new Date() });
        }

        // 2. ASSINATURA E PAGAMENTOS (Financeiro)
        const subQuery = query(collection(db, "subscriptions"), where("userId", "==", userRef), limit(1));
        const subSnapshot = await getDocs(subQuery);

        if (!subSnapshot.empty) {
            const subDoc = subSnapshot.docs[0];
            const subData = subDoc.data();
            const subId = subDoc.id;

            setSubscription(subData);

            // Busca valor real na sub-collection 'payments'
            try {
                const paymentsRef = collection(db, "subscriptions", subId, "payments");
                const paymentsQuery = query(paymentsRef, orderBy("createdAt", "desc"), limit(1));
                const paymentsSnap = await getDocs(paymentsQuery);

                if (!paymentsSnap.empty) {
                    const latestPayment = paymentsSnap.docs[0].data();
                    setLastPaymentValue(latestPayment.transactionAmount || 0);
                }
            } catch (err) {
                console.error("Erro ao buscar pagamentos:", err);
            }

            // 3. DETALHES DO PLANO (Features e Limites)
            if (subData.planId) {
                const planRef = typeof subData.planId === 'string' ? doc(db, "plans", subData.planId) : subData.planId;
                const planSnap = await getDoc(planRef);
                
                if (planSnap.exists()) {
                    setPlanDetails(planSnap.data());
                } else {
                    const pathSegments = planRef.path ? planRef.path.split('/') : [];
                    const planCode = pathSegments[pathSegments.length - 1] || "unknown";
                    setPlanDetails({ name: planCode.charAt(0).toUpperCase() + planCode.slice(1) });
                }
            }
        }

        // 4. DADOS OPERACIONAIS (Counts)
        const condoQuery = query(collection(db, "condominiums"), where("createdBy", "==", user.uid));
        const condoSnapshot = await getDocs(condoQuery);
        const condoCount = condoSnapshot.size;
        
        let condoIds = condoSnapshot.docs.map(doc => doc.id);
        let towerCount = 0; let areaCount = 0; let assetsCount = 0;

        if (condoIds.length > 0) {
            const searchIds = condoIds.slice(0, 10); 
            
            const towerQuery = query(collection(db, "towers"), where("condominiumId", "in", searchIds));
            const towerSnapshot = await getDocs(towerQuery);
            towerCount = towerSnapshot.size;

            const areaQuery = query(collection(db, "areas"), where("condominiumId", "in", searchIds));
            const areaSnapshot = await getDocs(areaQuery);
            areaCount = areaSnapshot.size;
            
            let areaIds = areaSnapshot.docs.map(doc => doc.id);
            if (areaIds.length > 0) {
                const searchAreaIds = areaIds.slice(0, 10);
                const assetsQuery = query(collection(db, "assets"), where("areaId", "in", searchAreaIds));
                const assetsSnapshot = await getDocs(assetsQuery);
                assetsCount = assetsSnapshot.size;
            }
        }

        setCounts({ condominiums: condoCount, towers: towerCount, areas: areaCount, assets: assetsCount });

      } catch (error) {
        console.error("❌ Erro ao buscar perfil:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  // --- HELPERS ---
  const formatDate = (dateValue) => {
    if (!dateValue) return "---";
    if (dateValue?.toDate) return dateValue.toDate().toLocaleDateString("pt-BR");
    if (typeof dateValue === "string") return new Date(dateValue).toLocaleDateString("pt-BR");
    return "---";
  };

  // 🔥 LÓGICA DE PREÇO CORRIGIDA 🔥
  const getDisplayPrice = () => {
      // 1. Se o backend marcou transactionAmount como 0 (cancelado/free), respeita isso IMEDIATAMENTE.
      // Isso sobrescreve qualquer histórico antigo de pagamento.
      if (subscription?.transactionAmount === 0) return 0;

      // 2. Se não for 0, aí sim olhamos se tem um pagamento recente na sub-collection
      if (lastPaymentValue > 0) return lastPaymentValue;
      
      // 3. Fallback para o preço de tabela do plano
      if (planDetails?.price > 0) return planDetails.price;
      
      return 0;
  };

  const formatCurrency = (val) => {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);
  };

  const translateStatus = (status) => {
      const map = { 'authorized': 'Ativo', 'active': 'Ativo', 'paused': 'Pausado', 'pending': 'Pendente', 'cancelled': 'Cancelado', 'free': 'Gratuito' };
      return map[status] || status || 'Inativo';
  };

  const getCardBrand = (type) => {
      if (!type) return "---";
      if (type === 'master') return "Mastercard";
      if (type === 'visa') return "Visa";
      return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const displayName = userData?.displayName || userData?.name || user?.displayName || "Usuário";
  const photoURL = userData?.photoURL || user?.photoURL;
  const userPlanName = planDetails?.name || (subscription ? "Plano Personalizado" : "Plano Gratuito");
  
  const planClass = userPlanName.toLowerCase().includes('pro') ? 'pro' : 
                    userPlanName.toLowerCase().includes('advanced') ? 'advanced' : 
                    userPlanName.toLowerCase().includes('free') ? 'free' : 'ultimate';

  const limits = planDetails?.limits || {};
  const features = planDetails?.features || limits.features || {};
  const maxCondos = planDetails?.maxCondominiums ?? limits.maxCondominiums ?? (planClass === 'free' ? 1 : 0);
  const maxTowers = planDetails?.maxTowers ?? limits.maxTowers ?? (planClass === 'free' ? 1 : 0);

  if (loading) return <div className="loading-container"><p>Carregando perfil...</p></div>;

  return (
    <div className="profile-container">
      <h2>Meu Perfil</h2>

      <div className="profile-header-card">
        <div className="profile-avatar-container">
            {photoURL ? (
                <img src={photoURL} alt="Foto de Perfil" className="profile-avatar" />
            ) : (
                <MdAccountCircle className="profile-avatar-placeholder" />
            )}
        </div>
        <div className="profile-info-primary">
            <h3>{displayName}</h3>
            <span className="profile-email">{userData?.email || user?.email}</span>
            <span className="profile-uid">UID: {user?.uid}</span>
        </div>
        <div>
             <button className="edit-profile-btn" disabled>Editar Perfil</button>
        </div>
      </div>

      <div className="profile-grid-layout">
        
        <div className="left-column">
            <div className="section-box plan-box">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h3 className="section-title" style={{ marginBottom: 0, border: 'none' }}>Assinatura</h3>
                    <span className={`plan-badge-large ${planClass}`}>
                        {userPlanName}
                    </span>
                </div>

                {subscription ? (
                    <div className="info-list">
                        <div className="info-item highlight">
                            <span className="label"><MdAttachMoney style={{ verticalAlign: 'middle' }}/> Valor Mensal:</span>
                            <span className="value">{formatCurrency(getDisplayPrice())}</span>
                        </div>
                        <div className="info-item">
                            <span className="label">Status:</span>
                            <span className="value" style={{ color: subscription.status === 'authorized' ? 'var(--success)' : 'inherit' }}>
                                {translateStatus(subscription.status)}
                            </span>
                        </div>
                        <div className="info-item">
                            <span className="label"><MdDateRange style={{ verticalAlign: 'middle' }}/> Próximo Pagamento:</span>
                            <span className="value">{formatDate(subscription.nextPaymentDate)}</span>
                        </div>
                        <div className="info-item">
                            <span className="label"><MdCreditCard style={{ verticalAlign: 'middle' }}/> Método:</span>
                            <span className="value">
                                {getCardBrand(subscription.paymentMethod?.type)} 
                                {subscription.paymentMethod?.last_four_digits ? ` •••• ${subscription.paymentMethod.last_four_digits}` : ''}
                            </span>
                        </div>
                    </div>
                ) : (
                    <div className="info-list">
                        <p className="plan-note">Você está utilizando a versão gratuita do sistema.</p>
                        <div className="info-item">
                            <span className="label">Status:</span>
                            <span className="value">Ativo (Free)</span>
                        </div>
                    </div>
                )}

                <button className="upgrade-btn" style={{ width: '100%', marginTop: '25px' }} onClick={() => onNavigate("SubscriptionManager")}>
                    {subscription ? "Gerenciar Assinatura" : "Fazer Upgrade Agora"}
                </button>
            </div>

            <div className="section-box">
                <h3 className="section-title">Limites do Plano</h3>
                <div className="info-list">
                    <div className="info-item">
                        <span className="label">Condomínios:</span>
                        <span className="value">{counts.condominiums} / {maxCondos > 1000 ? 'Ilimitado' : maxCondos}</span>
                    </div>
                    <div className="info-item">
                        <span className="label">Torres:</span>
                        <span className="value">{counts.towers} / {maxTowers > 1000 ? 'Ilimitado' : maxTowers}</span>
                    </div>
                    <div className="info-item">
                        <span className="label">Dashboard Avançado:</span>
                        <span className="value">{features.dashboardAccess ? <MdCheck color="green"/> : <MdClose color="red"/>}</span>
                    </div>
                    <div className="info-item">
                        <span className="label">Relatórios Avançados:</span>
                        <span className="value">{features.advancedReports ? <MdCheck color="green"/> : <MdClose color="red"/>}</span>
                    </div>
                </div>
            </div>
        </div>

        <div className="right-column">
            
            <div className="section-box">
                <h3 className="section-title">Resumo Operacional</h3>
                <div className="stats-mini-grid">
                    <div className="stat-card">
                        <strong>{counts.condominiums}</strong>
                        <span>Condomínios</span>
                    </div>
                    <div className="stat-card">
                        <strong>{counts.towers}</strong>
                        <span>Torres</span>
                    </div>
                    <div className="stat-card">
                        <strong>{counts.areas}</strong>
                        <span>Áreas</span>
                    </div>
                    <div className="stat-card">
                        <strong>{counts.assets}</strong>
                        <span>Ativos</span>
                    </div>
                </div>
            </div>

            <div className="section-box">
                <h3 className="section-title">Detalhes da Conta</h3>
                <div className="info-list">
                    <div className="info-item">
                        <span className="label">Membro desde:</span>
                        <span className="value">{formatDate(userData?.createdAt)}</span>
                    </div>
                    <div className="info-item">
                        <span className="label">Último Login:</span>
                        <span className="value">{formatDate(userData?.lastLogin)}</span>
                    </div>
                    <div className="info-item">
                        <span className="label">Idioma:</span>
                        <span className="value uppercase">{userData?.language || "PT-BR"}</span>
                    </div>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;